import { Component, ElementRef, inject, signal } from '@angular/core';
import {
  ActivitySectionDirective, IMenuWithPages, MenusService, RestaurantTableService,
  UserActivityClickTrackingDirective,
} from '@core';
import {
  PageHeaderBackSwipeConfig,
  PageHeaderComponent,
} from '@app/components/page-header/page-header.component';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LoadingComponent } from '@app/components/loading/loading.component';
import { PagesComponent } from '@app/pages/main-page/menu-tab/pages/pages.component';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-menus-tab',
  templateUrl: 'menu-tab.component.html',
  styleUrl: 'menu-tab.component.scss',
  imports: [
    ActivitySectionDirective,
    PageHeaderComponent,
    UserActivityClickTrackingDirective,
    RouterLink,
    LoadingComponent,
    PagesComponent,
  ],
  host: {
    'class': 'page',
  },
  animations: [
    trigger('InOutAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('400ms ease-in-out', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [
        animate('400ms ease-in-out', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
})
export class MenuTabComponent {

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly translate = inject(TranslateService);
  private readonly restaurantTable = inject(RestaurantTableService);
  private readonly menusService = inject(MenusService);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    direction: 'vertical',
    element: this.elementRef.nativeElement,
  };

  private readonly menus$ = combineLatest([
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.getCurrentLang()),
    ),
    this.menusService.menusWithPagesAndIcons$,
  ]).pipe(
    map(([language, menus]) => {
      if (menus === undefined) {
        return [];
      }

      return menus.filter((menu) => {
        if (menu.hidden || menu.happyHours) {
          return false;
        }

        return !menu.language || menu.language === language;
      });
    }),
  );

  public readonly tableName = toSignal(this.restaurantTable.table$.pipe(
    map((table) => {
      return table ? table.tableName : undefined;
    }),
  ));

  public readonly loading = toSignal(this.menusService.loading$, {
    requireSync: true,
  });

  public readonly menus = toSignal(this.menus$, {
    initialValue: [],
  });

  public readonly selected = signal<IMenuWithPages | null>(null);

}
